import { Link } from 'react-router-dom'
import logoWhite from '@assets/images/Logo/Logo_White.png'
import { productCategories } from '@data/categories'
import { suppliers }         from '@data/suppliers'
import { companyInfo }       from '@data/companyInfo'

// ── Static data ───────────────────────────────────────────────────────────────
const QUICK_LINKS = [
  { label: 'Home',        to: '/' },
  { label: 'About Us',    to: '/about' },
  { label: 'Products',    to: '/products' },
  { label: 'News & Blog', to: '/blog' },
  { label: 'Contact Us',  to: '/contact' },
]

const SOCIALS = [
  {
    label: 'Facebook', href: 'https://facebook.com',
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/></svg>,
  },
  {
    label: 'Instagram', href: 'https://instagram.com',
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/></svg>,
  },
  {
    label: 'LinkedIn', href: 'https://linkedin.com',
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-4 0v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/><circle cx="4" cy="4" r="2"/></svg>,
  },
  {
    label: 'WhatsApp', href: 'https://wa.me/94718919034',
    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.126 1.533 5.859L.057 23.478a.75.75 0 00.916.919l5.701-1.464A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75a9.695 9.695 0 01-4.953-1.355l-.355-.211-3.683.945.973-3.596-.231-.37A9.718 9.718 0 012.25 12C2.25 6.615 6.615 2.25 12 2.25S21.75 6.615 21.75 12 17.385 21.75 12 21.75z"/></svg>,
  },
]

// ── Helpers ───────────────────────────────────────────────────────────────────
const TEXT   = 'rgba(255,255,255,0.85)'
const MUTED  = 'rgba(255,255,255,0.60)'
const FAMILY = "'DM Sans', sans-serif"

function ColHeading({ children }) {
  return (
    <div style={{ marginBottom: '20px' }}>
      <h4 style={{ fontFamily: FAMILY, fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.13em', textTransform: 'uppercase', color: '#ed9716', margin: '0 0 10px' }}>
        {children}
      </h4>
      <div style={{ width: '24px', height: '2px', background: '#ed9716' }} />
    </div>
  )
}

function FLink({ to, children }) {
  return (
    <Link
      to={to}
      style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '4px 0', fontSize: '13.5px', fontWeight: 400, fontFamily: FAMILY, color: TEXT, textDecoration: 'none', transition: 'color 0.2s, padding-left 0.2s' }}
      onMouseEnter={e => { e.currentTarget.style.color = '#ed9716'; e.currentTarget.style.paddingLeft = '5px' }}
      onMouseLeave={e => { e.currentTarget.style.color = TEXT; e.currentTarget.style.paddingLeft = '0' }}
    >
      <svg width="9" height="9" viewBox="0 0 9 9" fill="none" style={{ flexShrink: 0, opacity: 0.55 }}>
        <path d="M1 4.5h7M4.5 1.5L7.5 4.5l-3 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      {children}
    </Link>
  )
}

function IconBox({ children }) {
  return (
    <div style={{ width: '32px', height: '32px', flexShrink: 0, background: 'rgba(237,151,22,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {children}
    </div>
  )
}

// ── Main Footer ───────────────────────────────────────────────────────────────
export default function Footer() {
  const year = new Date().getFullYear()

  return (
    <footer style={{ fontFamily: FAMILY }}>
      <style>{`
        .f-grid {
          display: grid;
          grid-template-columns: 1.8fr 1fr 1.2fr 1.2fr;
          gap: 52px;
        }
        @media (max-width: 1023px) { .f-grid { grid-template-columns: 1fr 1fr; gap: 36px; } }
        @media (max-width: 599px)  { .f-grid { grid-template-columns: 1fr; gap: 28px; } }

        .f-bottom-row {
          display: flex; align-items: center;
          justify-content: space-between; flex-wrap: wrap; gap: 12px;
        }
        @media (max-width: 639px) { .f-bottom-row { flex-direction: column; align-items: flex-start; gap: 10px; } }

        .f-social-btn {
          width: 38px; height: 38px;
          display: flex; align-items: center; justify-content: center;
          background: rgba(255,255,255,0.10);
          color: rgba(255,255,255,0.75);
          text-decoration: none;
          transition: background 0.2s ease, color 0.2s ease;
        }
        .f-social-btn:hover { background: #ed9716; color: #ffffff; }

.f-bottom-link {
          font-size: 12px; color: rgba(255,255,255,0.50);
          text-decoration: none; transition: color 0.2s ease; font-family: 'DM Sans', sans-serif;
          padding: 0 12px;
        }
        .f-bottom-link:hover { color: rgba(255,255,255,0.85); }
      `}</style>

      {/* ── Main Body ── */}
      <div style={{ background: '#065e32' }}>
        <div style={{ maxWidth: 'var(--container-max)', margin: '0 auto', padding: '60px var(--container-padding) 48px' }}>
          <div className="f-grid">

            {/* Col 1: Brand + Contact */}
            <div>
              <img src={logoWhite} alt="Salient Agrinutrition"
                style={{ height: '50px', width: 'auto', objectFit: 'contain', maxWidth: '190px', marginBottom: '18px' }} />

              <p style={{ fontSize: '13.5px', color: TEXT, lineHeight: 1.8, maxWidth: '290px', margin: '0 0 28px' }}>
                A globally connected livestock nutrition partner committed to advancing Sri Lanka's animal production sector through science-driven solutions.
              </p>

              {/* Office */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '14px' }}>
                <IconBox>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ed9716" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                </IconBox>
                <div>
                  <p style={{ fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: '#ed9716', margin: '0 0 4px' }}>Office</p>
                  <p style={{ fontSize: '13px', color: TEXT, margin: 0, lineHeight: 1.75 }}>
                    No.51/D, P.T.S. Avenue,<br />Dalugama, Kelaniya, Sri Lanka
                  </p>
                </div>
              </div>

              {/* Warehouse */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '14px' }}>
                <IconBox>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ed9716" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                    <polyline points="9 22 9 12 15 12 15 22"/>
                  </svg>
                </IconBox>
                <div>
                  <p style={{ fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: '#ed9716', margin: '0 0 4px' }}>Warehouse</p>
                  <p style={{ fontSize: '13px', color: TEXT, margin: 0, lineHeight: 1.75 }}>
                    Epaladeniya Waththa,<br />Off-Hettipola Road,<br />Epaladeniya, Kuliyapitiya
                  </p>
                </div>
              </div>

              {/* Phone */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '24px' }}>
                <IconBox>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ed9716" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z"/>
                  </svg>
                </IconBox>
                <div>
                  <p style={{ fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: '#ed9716', margin: '0 0 4px' }}>Phone</p>
                  {companyInfo.phones.map(p => (
                    <a key={p.number} href={`tel:${p.number}`}
                      style={{ fontSize: '13px', color: TEXT, textDecoration: 'none', display: 'block', lineHeight: 1.75, transition: 'color 0.2s' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#ed9716'}
                      onMouseLeave={e => e.currentTarget.style.color = TEXT}>
                      {p.display}
                    </a>
                  ))}
                </div>
              </div>

              {/* Socials */}
              <div style={{ display: 'flex', gap: '6px' }}>
                {SOCIALS.map(({ label, href, icon }) => (
                  <a key={label} href={href} target="_blank" rel="noopener noreferrer"
                    aria-label={label} className="f-social-btn">
                    {icon}
                  </a>
                ))}
              </div>
            </div>

            {/* Col 2: Quick Links */}
            <div>
              <ColHeading>Quick Links</ColHeading>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {QUICK_LINKS.map(({ label, to }) => (
                  <FLink key={label} to={to}>{label}</FLink>
                ))}
              </div>
            </div>

            {/* ── Col 3: Products — now from real categories.js ── */}
            <div>
              <ColHeading>Products</ColHeading>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {productCategories.map(cat => (
                  <FLink key={cat.id} to={`/products?category=${cat.id}`}>
                    {cat.label}
                  </FLink>
                ))}
              </div>
            </div>

            {/* ── Col 4: Global Suppliers — plain text, same style as Quick Links / Products ── */}
            <div>
              <ColHeading>Global Suppliers</ColHeading>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {suppliers.map(s => (
                  <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '4px 0', fontSize: '13.5px', fontWeight: 400, fontFamily: FAMILY, color: TEXT }}>
                    <svg width="9" height="9" viewBox="0 0 9 9" fill="none" style={{ flexShrink: 0, opacity: 0.55 }}>
                      <path d="M1 4.5h7M4.5 1.5L7.5 4.5l-3 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {s.name}
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Divider */}
      <div style={{ background: '#054d2a', height: '1px' }} />

      {/* Bottom Bar */}
      <div style={{ background: '#044020' }}>
        <div style={{ maxWidth: 'var(--container-max)', margin: '0 auto', padding: '14px var(--container-padding)' }}>
          <div className="f-bottom-row">
            <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.50)', margin: 0, fontFamily: FAMILY }}>
              © {year} Salient Agrinutrition Pvt. Ltd. All rights reserved.
            </p>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {[
                { label: 'Privacy Policy', to: '/privacy-policy' },
                { label: 'Terms of Use',   to: '/terms-of-use'   },
                { label: 'Sitemap',        to: '/sitemap'         },
              ].map(({ label, to }, i, arr) => (
                <span key={label} style={{ display: 'flex', alignItems: 'center' }}>
                  <Link to={to} className="f-bottom-link">{label}</Link>
                  {i < arr.length - 1 && <span style={{ color: 'rgba(255,255,255,0.18)', fontSize: '11px' }}>|</span>}
                </span>
              ))}
            </div>
            <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.28)', margin: 0, fontFamily: FAMILY }}>
              Designed by <span style={{ color: 'rgba(255,255,255,0.50)' }}>OORA Labs</span>
            </p>
          </div>
        </div>
      </div>
    </footer>
  )
}